<!-- src/components/trends/AtRiskPanel.vue -->
<!-- 회원권 추세 "이탈 위험" 탭: 오늘 연락할 회원 목록(이유 배지·최근 연락·[연락 기록]) -->
<!-- (docs/architecture/admin-trends-design.md 1.5·2.2·4.4절). 불러오기·오류는 TrendsView가, 이 패널은 받은 값만 그린다 -->
<template>
  <div class="ar-panel">
    <div v-if="!data" class="state">불러오는 중…</div>

    <div v-else-if="rows.length === 0" class="state">
      지금 연락이 필요한 회원이 없습니다.
    </div>

    <template v-else>
      <!-- 거르기: 이유(서버 summary 수) · 연락 상태(화면 계산 수). 화면 안에서만 거르고 서버를 다시 부르지 않는다 -->
      <div class="ar-filters">
        <div class="filter-line">
          <span class="filter-k">이유</span>
          <div class="filter-group" role="group" aria-label="이탈 위험 이유">
            <button
              v-for="f in reasonFilters"
              :key="f.value"
              type="button"
              :class="['filter-btn', { active: reasonValue === f.value }]"
              :aria-pressed="reasonValue === f.value"
              @click="setReason(f.value)"
            >
              {{ f.label }}
              <span class="count">{{ countText(f.count) }}</span>
            </button>
          </div>
          <span class="filter-note">{{ reasonCountNote }}</span>
        </div>

        <div class="filter-line">
          <span class="filter-k">연락</span>
          <div class="filter-group" role="group" aria-label="연락 상태">
            <button
              v-for="f in contactFilters"
              :key="f.value"
              type="button"
              :class="['filter-btn', { active: contactValue === f.value }]"
              :aria-pressed="contactValue === f.value"
              @click="setContact(f.value)"
            >
              {{ f.label }}
              <span class="count">{{ countText(f.count) }}</span>
            </button>
          </div>
          <span class="filter-note">최근 7일 연락 기록 기준</span>
        </div>

        <!-- 보이는 짧은 정의: 고른 이유 하나, "전체"면 다섯 이유 모두(툴팁만이 아니다, 설계 4.9절) -->
        <ul class="ar-defs" aria-label="이유 정의">
          <li v-for="d in reasonDefinitions" :key="d.value">
            <b>{{ d.label }}</b> {{ d.text }}
          </li>
        </ul>
      </div>

      <div v-if="filteredRows.length === 0" class="state">
        이 조건에 해당하는 회원이 없습니다.
      </div>

      <!-- 두 묶음: 오늘 연락(펼침) · 지켜보기(접어 둠, 누르면 펼침) -->
      <template v-else>
        <section
          v-for="g in groups"
          :key="g.key"
          :class="['ar-tier', `ar-tier--${g.key}`]"
        >
          <h3 v-if="g.key === 'urgent'" class="tier-head">
            <span class="tier-title">{{ g.label }}</span>
            <span class="tier-count">{{ g.rows.length }}명</span>
            <span class="tier-note">빨간 배지가 있거나 이유가 둘 이상</span>
          </h3>
          <h3 v-else class="tier-head">
            <button
              type="button"
              class="tier-toggle"
              :aria-expanded="watchOpen"
              :disabled="g.rows.length === 0"
              @click="toggleWatch"
            >
              <span class="caret" aria-hidden="true">{{
                g.open ? "▾" : "▸"
              }}</span>
              <span class="tier-title">{{ g.label }}</span>
              <span class="tier-count">{{ g.rows.length }}명</span>
              <span class="tier-note">{{
                g.rows.length === 0
                  ? "급하지 않은 이유 하나"
                  : g.open
                  ? "누르면 접습니다"
                  : "급하지 않은 이유 하나 · 누르면 펼칩니다"
              }}</span>
            </button>
          </h3>

          <p
            v-if="g.key === 'urgent' && g.rows.length === 0"
            class="tier-empty"
          >
            {{
              filtersActive
                ? "이 조건에 해당하는 오늘 연락 회원이 없습니다."
                : "오늘 연락할 회원이 없습니다."
            }}
          </p>

          <div
            v-if="g.open && g.rows.length > 0"
            class="ar-table"
            role="table"
            :aria-label="`${g.label} 회원 목록`"
          >
            <div class="ar-row ar-head" role="row">
              <div class="cell" role="columnheader">이름 · 전화</div>
              <div class="cell" role="columnheader">이유</div>
              <div class="cell" role="columnheader">수강권</div>
              <div class="cell h-attendance" role="columnheader">최근 출석</div>
              <div class="cell" role="columnheader">최근 연락</div>
              <div class="cell h-note" role="columnheader">특이사항</div>
              <div class="cell" role="columnheader">
                <span class="sr-only">연락 기록</span>
              </div>
            </div>

            <div
              v-for="row in g.rows"
              :key="row.user_id"
              class="ar-row ar-item"
              :class="{ done: isDimmed(row) }"
              role="row"
              tabindex="0"
              @click="openProfile(row, $event)"
              @auxclick="openProfile(row, $event)"
              @mousedown.middle.prevent
              @keydown.enter.self="openProfile(row, $event)"
            >
              <!-- 이름 · 전화 -->
              <div class="cell cell-name" role="cell">
                <div class="primary name-line">
                  <span class="name" :title="row.name || null">{{
                    row.name || "이름 없음"
                  }}</span>
                  <!-- 표에서는 "방금"만(이름 옆 자리가 좁아 긴 말은 줄을 한 줄 늘렸다), 카드에서는 다 보인다. 말 전체는 title과
                       화면 읽기 프로그램에 -->
                  <span
                    v-if="showSavedBadge(row)"
                    class="badge just-saved"
                    :class="toneClass(justSavedBadge(row.contact).tone)"
                    :title="`${
                      justSavedBadge(row.contact).text
                    } · 이 화면에서 방금 연락 기록을 남겼습니다`"
                    ><span class="just-saved-short" aria-hidden="true"
                      >방금</span
                    ><span class="just-saved-full">{{
                      justSavedBadge(row.contact).text
                    }}</span></span
                  >
                </div>
                <div class="secondary">
                  {{ prettyPhone(row.phone_number) || "—" }}
                </div>
              </div>

              <!-- 이유 배지(색은 서버 level) + 휴회 중·다음 예약 칩 -->
              <div class="cell cell-reasons" role="cell">
                <span class="cell-k">이유</span>
                <div class="badges">
                  <span
                    v-for="r in row.reasons || []"
                    :key="r.code"
                    class="badge"
                    :class="toneClass(reasonTone(r))"
                    :title="reasonTitle(r)"
                    >{{ reasonText(r, row) }}</span
                  >
                </div>
                <div v-if="holdText(row) || nextBookingText(row)" class="chips">
                  <span v-if="holdText(row)" class="chip tone-info">{{
                    holdText(row)
                  }}</span>
                  <span v-if="nextBookingText(row)" class="chip">{{
                    nextBookingText(row)
                  }}</span>
                </div>
              </div>

              <!-- 수강권 -->
              <div class="cell cell-membership" role="cell">
                <span class="cell-k">수강권</span>
                <div
                  v-if="row.membership"
                  class="primary wrap"
                  :class="{ ended: row.membership.state === 'ended' }"
                  :title="membershipTitle(row.membership)"
                >
                  {{ membershipCellText(row.membership) }}
                </div>
                <div v-else class="primary dim">—</div>
              </div>

              <!-- 최근 출석 -->
              <div class="cell cell-attendance" role="cell">
                <span class="cell-k">최근 출석</span>
                <div v-if="row.last_attendance" class="primary">
                  {{ attendanceDateText(row.last_attendance, today) }}
                </div>
                <div v-else class="primary dim" :title="noAttendanceTitle()">
                  없음
                </div>
              </div>

              <!-- 최근 연락: "10.14 김코치 · 통화"(결과 색), 데스크톱은 내용 앞 30자, 30일보다 오래되면 회색 "n개월 전" -->
              <div
                class="cell cell-contact"
                role="cell"
                :title="
                  row.contact && row.contact.content
                    ? row.contact.content
                    : null
                "
              >
                <span class="cell-k">최근 연락</span>
                <template v-if="row.contact">
                  <div class="primary contact-line">
                    <span>{{ contactWho(row.contact) }}</span>
                    <span
                      v-if="contactResultLabel(row.contact.result)"
                      :class="`text-${contactResultTone(row.contact.result)}`"
                    >
                      · {{ contactResultLabel(row.contact.result) }}</span
                    >
                    <span
                      v-if="contactAgeText(row.contact, today)"
                      class="contact-age"
                      >{{ contactAgeText(row.contact, today) }}</span
                    >
                  </div>
                  <div
                    v-if="contactSnippet(row.contact)"
                    class="secondary snippet"
                  >
                    {{ contactSnippet(row.contact) }}
                  </div>
                  <!-- 카드에서만: 마우스 올림(title)이 없으므로 내용을 두 줄까지 -->
                  <div v-if="row.contact.content" class="contact-content">
                    {{ row.contact.content }}
                  </div>
                </template>
                <div v-else class="primary text-danger">연락 없음</div>
              </div>

              <!-- 특이사항(서버가 첫 줄만, 최대 100자) -->
              <div class="cell cell-note" role="cell">
                <span class="cell-k">특이사항</span>
                <div
                  v-if="noteOf(row)"
                  class="primary note-text"
                  :title="noteOf(row)"
                >
                  {{ noteOf(row) }}
                </div>
                <div v-else class="primary dim">—</div>
              </div>

              <!-- 연락 기록 버튼: 줄 이동을 막는다 -->
              <div class="cell cell-action" role="cell">
                <button
                  type="button"
                  class="log-btn"
                  @click.stop="openContactLog(row)"
                  @auxclick.stop
                >
                  연락 기록
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>

      <p v-if="data.truncated" class="ar-truncated">
        급한 순 500명까지 보입니다.
      </p>
    </template>

    <!-- 모달은 body로 옮겨 그린다: 이 패널은 [새로고침] 동안 흐린(opacity) 상자 안에 있어, 그 사이 연 모달과 덮개까지
         비쳐 보였다(리뷰 3차) -->
    <Teleport to="body">
      <ContactLogModal
        v-if="logMember"
        ref="contactLog"
        :member="logMember"
        @close="logMember = null"
        @saved="onContactSaved"
      />
    </Teleport>
  </div>
</template>

<script>
import ContactLogModal from "../ContactLogModal.vue";
import {
  contactResultLabel,
  contactResultTone,
  dotDate,
  membershipCountText,
  noteDate,
  prettyPhone,
} from "@/lib/memberNotes";
import {
  ATTENDANCE_LOOKBACK_DAYS,
  CONTACT_FILTERS,
  DEFAULT_CONTACT,
  DEFAULT_REASON,
  METRIC_DEFINITIONS,
  RISK_REASONS,
  RISK_TIERS,
  attendanceDateText,
  contactAgeText,
  contactFromNote,
  contactSnippet,
  contactState,
  filterAtRiskRows,
  holdText,
  justSavedBadge,
  membershipCellText,
  membershipTypeLabel,
  membershipUsedUp,
  nextBookingText,
  normalizeContact,
  normalizeReason,
  orderAtRiskRows,
  reasonText,
  reasonTone,
  rowsByTier,
  shortDefinitionOf,
} from "@/lib/trends";

// 지켜보기 펼침은 이 방문 기록 항목(history.state)에 둔다: 회원 상세에 갔다가 뒤로 오면 화면이 새로 만들어지는데, 펼친 채로
// 그려야 페이지 높이가 같아 스크롤 복원(router/index.js)이 보던 줄을 찾는다(리뷰 2차: 접힌 채 그려져 페이지 끝으로 갔다).
// 주소에는 담지 않는다(공유하는 주소가 아니라 화면 상태). 사이드메뉴로 새로 들어온 항목은 접힌 채 시작한다
const WATCH_STATE_KEY = "trendsWatchOpen";
function readWatchOpen() {
  try {
    return !!(window.history.state && window.history.state[WATCH_STATE_KEY]);
  } catch (e) {
    return false;
  }
}
function saveWatchOpen(open) {
  try {
    const st = window.history.state || {};
    if (!!st[WATCH_STATE_KEY] === open) return;
    window.history.replaceState({ ...st, [WATCH_STATE_KEY]: open }, "");
  } catch (e) {
    // 방문 기록에 못 쓰면 이 화면 안에서만 지킨다
  }
}

// 이 화면에서 모달로 저장한 줄(자리 지킴·"방금" 배지)과 그 연락도 같은 방문 기록 항목에 둔다: 회원 상세에 갔다가 뒤로 오면 화면이
// 새로 만들어지고 서버 값에는 그 연락이 "연락함"으로 와서, 그 줄들이 묶음 아래로 내려가 목록이 저장한 줄 수만큼 올라갔다(리뷰 3차:
// 스크롤 복원이 보던 회원 대신 다른 회원 위에 섰다). 이 문서를 연 동안만 쓴다(새로 고침하면 새 방문이라 연락함 줄은 아래로).
//   justSaved: user_id → { part: 저장 전에 있던 쪽 "head" | "tail", at: 저장한 기록의 시각,
//                          filter: 저장할 때 고른 연락 거르기(그 거르기에서만 빠지지 않는다, 거르기를 바꾸면 "") } (자리·배지)
//   savedContacts: user_id → { contact, ms: 저장한 때(브라우저 시계) } (서버 값을 받기 전까지만 쓰는 대역)
const SAVED_STATE_KEY = "trendsJustSaved";
const PAGE_LOAD_ID = `${Date.now()}-${Math.random()}`;
function emptySaved() {
  return { justSaved: {}, savedContacts: {} };
}
function readSaved() {
  try {
    const v = window.history.state && window.history.state[SAVED_STATE_KEY];
    if (!v || v.page !== PAGE_LOAD_ID) return emptySaved();
    return {
      justSaved: { ...(v.justSaved || {}) },
      savedContacts: { ...(v.savedContacts || {}) },
    };
  } catch (e) {
    return emptySaved();
  }
}
function saveSaved(justSaved, savedContacts) {
  try {
    if (Object.keys(justSaved).length === 0) return;
    const st = window.history.state || {};
    // 반응형 프록시는 방문 기록에 담기지 않으므로 평범한 값으로 바꾼다
    const value = JSON.parse(
      JSON.stringify({ page: PAGE_LOAD_ID, justSaved, savedContacts })
    );
    window.history.replaceState({ ...st, [SAVED_STATE_KEY]: value }, "");
  } catch (e) {
    // 방문 기록에 못 쓰면 이 화면 안에서만 지킨다
  }
}

// 저장한 연락은 서버 값을 받기 전까지만 대신 보인다: 저장한 뒤에 부른 응답이고(requestedAt > ms) 서버가 연락 기록 쓰기를 모두
// 반영했으면(contacts_fresh) 서버 값을 그대로 쓴다. 리뷰 4차: 늘 더 늦은 쪽을 보여, 회원 상세에서 지운 기록이 뒤로 와도
// [새로고침]해도 "방금 연락함"으로 남고 연락함 수에 들었다
function serverHasSave(data, requestedAt, saved) {
  return (
    !!data &&
    data.contacts_fresh !== false &&
    Number.isFinite(requestedAt) &&
    !!saved &&
    requestedAt > saved.ms
  );
}

// 같은 회원의 두 연락 중 더 늦은 것("YYYY-MM-DD HH:mm"는 글자 순서가 곧 시간 순서). 같으면 서버 값
function laterContact(server, saved) {
  if (!server) return saved;
  if (!saved) return server;
  return String(saved.at || "") > String(server.at || "") ? saved : server;
}

export default {
  name: "AtRiskPanel",
  components: { ContactLogModal },
  props: {
    // GET /v1/admin/trends/at-risk 응답(설계 2.2절)
    data: { type: Object, default: null },
    // 이유 거르기 (?reason=, all·expiring·grace·low_count·absent·churned)
    reason: { type: String, default: DEFAULT_REASON },
    // 연락 상태 거르기 (?contact=, all·none·retry·done)
    contact: { type: String, default: DEFAULT_CONTACT },
    // data를 부른 때(브라우저 시계 ms, TrendsView). 저장한 연락보다 뒤에 부른 응답인지 가린다
    requestedAt: { type: Number, default: 0 },
  },
  // update:reason·update:contact: 거르기 → URL(TrendsView). contact-saved: { userId, note } (모달이 만든 기록, 다시 읽지 않음)
  emits: ["update:reason", "update:contact", "contact-saved"],
  data() {
    const saved = readSaved();
    return {
      // 지켜보기 묶음 펼침(화면 상태라 주소에 담지 않고 이 방문 기록 항목에 둔다)
      watchOpen: readWatchOpen(),
      // [연락 기록] 모달 대상 줄
      logMember: null,
      // 이 화면에서 모달로 저장한 연락: user_id → { contact(contactFromNote 모양), ms }. 저장 뒤에 부른 서버 값을 받으면 버린다
      // (serverHasSave). 아래 justSaved와 함께 이 방문 기록 항목에도 둔다(회원 상세에서 뒤로 와도 그대로)
      savedContacts: saved.savedContacts,
      // 방금 저장한 줄: user_id → { part, at, filter }. 저장 전에 있던 쪽(위·아래)에서 자리를 지키고(orderAtRiskRows keep)
      // 저장할 때 고른 연락 거르기에서는 빠지지 않는다
      justSaved: saved.justSaved,
    };
  },
  computed: {
    today() {
      return (this.data && this.data.today) || "";
    },
    summary() {
      return (this.data && this.data.summary) || null;
    },
    // 서버 줄 + 이 화면에서 저장한 연락(줄을 복사해 덮는다. 받은 응답은 바꾸지 않음). 저장 뒤에 부른 서버 값이 오면 서버 값
    rows() {
      const list =
        this.data && Array.isArray(this.data.members) ? this.data.members : [];
      if (Object.keys(this.savedContacts).length === 0) return list;
      return list.map((row) => {
        const saved = this.savedContacts[row.user_id];
        if (!saved || serverHasSave(this.data, this.requestedAt, saved))
          return row;
        return { ...row, contact: laterContact(row.contact, saved.contact) };
      });
    },
    reasonValue() {
      return normalizeReason(this.reason);
    },
    contactValue() {
      return normalizeContact(this.contact);
    },
    // 방금 저장한 줄이 지킬 쪽: user_id → "head" | "tail"
    keepParts() {
      const m = new Map();
      for (const [id, v] of Object.entries(this.justSaved)) {
        m.set(id, v && v.part === "tail" ? "tail" : "head");
      }
      return m;
    },
    filtersActive() {
      return (
        this.reasonValue !== DEFAULT_REASON ||
        this.contactValue !== DEFAULT_CONTACT
      );
    },

    // 이유 버튼 수는 서버 summary(500줄 상한 전, 한 회원이 여러 이유면 각각). 없으면 받은 줄로 센다
    reasonFilters() {
      const s = this.summary;
      const countOf = (code) => {
        if (s && Number.isFinite(s[code])) return s[code];
        return this.rows.filter((row) =>
          (row.reasons || []).some((r) => r.code === code)
        ).length;
      };
      const total = s && Number.isFinite(s.total) ? s.total : this.rows.length;
      return [
        { value: DEFAULT_REASON, label: "전체", count: total },
        ...RISK_REASONS.map((r) => ({
          value: r.value,
          label: r.label,
          count: countOf(r.value),
        })),
      ];
    },
    reasonCountNote() {
      return METRIC_DEFINITIONS.reason_count;
    },
    // 고른 이유로 거른 줄(연락 거르기 전). 연락 버튼 수의 바탕
    reasonRows() {
      return filterAtRiskRows(this.rows, {
        reason: this.reasonValue,
        today: this.today,
      });
    },
    // 연락 버튼 수는 화면 계산(고른 이유 안에서)
    contactFilters() {
      const counts = { none: 0, retry: 0, done: 0 };
      for (const row of this.reasonRows) {
        counts[contactState(row.contact, this.today)] += 1;
      }
      return [
        {
          value: DEFAULT_CONTACT,
          label: "전체",
          count: this.reasonRows.length,
        },
        ...CONTACT_FILTERS.map((c) => ({
          value: c.value,
          label: c.label,
          count: counts[c.value],
        })),
      ];
    },
    reasonDefinitions() {
      const list =
        this.reasonValue === DEFAULT_REASON
          ? RISK_REASONS
          : RISK_REASONS.filter((r) => r.value === this.reasonValue);
      return list.map((r) => ({
        value: r.value,
        label: r.label,
        text: shortDefinitionOf(r.value),
      }));
    },
    // 연락 거르기. 방금 모달로 저장한 줄은 저장할 때 고른 거르기에서만 상태가 바뀌어도 남긴다(저장하자마자 줄이 사라지지 않게).
    // 다른 거르기에서는 실제 연락 상태로 거른다(리뷰 5차: 통화를 저장한 회원이 "부재 · 다시 필요"·"연락 안 함"에도 남아 목록이
    // 버튼 수와 달랐다)
    filteredRows() {
      if (this.contactValue === DEFAULT_CONTACT) return this.reasonRows;
      return this.reasonRows.filter((row) => {
        const js = this.justSaved[row.user_id];
        return (
          (js && js.filter === this.contactValue) ||
          contactState(row.contact, this.today) === this.contactValue
        );
      });
    },
    // 서버 tier로 두 묶음, 묶음 안은 서버 정렬에 "최근 7일 연락함" 줄만 아래로(방금 저장한 줄은 자리 그대로)
    groups() {
      const tiers = rowsByTier(this.filteredRows);
      return RISK_TIERS.map((t) => ({
        key: t.value,
        label: t.label,
        rows: orderAtRiskRows(tiers[t.value], this.today, this.keepParts),
        open: t.value === "urgent" ? true : this.watchOpen,
      }));
    },
  },
  watch: {
    // 연락 거르기를 바꾸면 방금 저장한 줄도 실제 연락 상태로 거른다(자리·배지는 그대로). 회원 상세에서 뒤로 와 다시 그릴 때는
    // 거르기가 바뀌지 않아 저장할 때의 거르기에서 그대로 남는다
    contactValue() {
      let changed = false;
      const next = {};
      for (const [id, v] of Object.entries(this.justSaved)) {
        if (v && v.filter) {
          next[id] = { ...v, filter: "" };
          changed = true;
        } else next[id] = v;
      }
      if (!changed) return;
      this.justSaved = next;
      if (this.$route.name === "Trends")
        saveSaved(this.justSaved, this.savedContacts);
    },
    // 저장 뒤에 부른 서버 값이 오면 그 회원의 대역 연락을 버린다(방문 기록 항목에서도). 자리·배지(justSaved)는 그대로
    data() {
      const keep = {};
      let dropped = false;
      for (const [id, saved] of Object.entries(this.savedContacts)) {
        if (serverHasSave(this.data, this.requestedAt, saved)) dropped = true;
        else keep[id] = saved;
      }
      if (!dropped) return;
      this.savedContacts = keep;
      if (this.$route.name === "Trends")
        saveSaved(this.justSaved, this.savedContacts);
    },
    // 같은 화면에서 주소만 바뀌면(거르기·탭은 replace, 사이드메뉴는 push로 새 항목) 지금 펼침을 그 항목에도 적는다.
    // 다른 화면으로 나가는 중(회원 상세 등)이면 그 항목에 쓰지 않는다
    "$route.fullPath"() {
      if (this.$route.name !== "Trends") return;
      saveWatchOpen(this.watchOpen);
      saveSaved(this.justSaved, this.savedContacts);
    },
  },
  methods: {
    prettyPhone,
    contactResultLabel,
    contactResultTone,
    attendanceDateText,
    contactAgeText,
    contactSnippet,
    holdText,
    justSavedBadge,
    nextBookingText,
    membershipCellText,
    reasonText,
    reasonTone,

    countText(n) {
      return Number.isFinite(n) ? String(n) : "–";
    },
    // 최근 출석 "없음"의 뜻: 서버는 최근 60일 안에서만 찾는다(DB 감사)
    noAttendanceTitle() {
      return `최근 ${ATTENDANCE_LOOKBACK_DAYS}일 안에 출석 없음`;
    },
    toneClass(tone) {
      return tone ? `tone-${tone}` : "";
    },

    toggleWatch() {
      this.watchOpen = !this.watchOpen;
      saveWatchOpen(this.watchOpen);
    },
    setReason(value) {
      if (value !== this.reasonValue) this.$emit("update:reason", value);
    },
    setContact(value) {
      if (value !== this.contactValue) this.$emit("update:contact", value);
    },

    isJustSaved(row) {
      return !!this.justSaved[row.user_id];
    },
    // "방금" 배지는 줄의 최근 연락이 저장한 기록(이나 그 뒤 것)일 때만: 그 기록을 회원 상세에서 지웠으면 서버 값이 앞 연락이나
    // 없음이라 배지를 떼고(자리는 지킨다), 고쳤으면 고친 결과로 단다
    showSavedBadge(row) {
      const js = this.justSaved[row.user_id];
      if (!js || !row.contact) return false;
      return String(row.contact.at || "") >= String(js.at || "");
    },
    // 최근 7일 안에 연락이 된(통화·문자) 줄은 흐리게. 방금 저장한 줄은 그대로. 연락함만 거른 목록은 모두 연락함이라
    // 흐리게 할 까닭이 없다(리뷰 4차: 목록 전체가 흐렸다)
    isDimmed(row) {
      return (
        this.contactValue !== "done" &&
        !this.isJustSaved(row) &&
        contactState(row.contact, this.today) === "done"
      );
    },
    reasonTitle(r) {
      return shortDefinitionOf(r.code);
    },
    noteOf(row) {
      return String(row.admin_note || "").trim();
    },
    // 수강권 칸 마우스 올림: "10회권 · 4/10회 · 2026.08.20 ~ 2026.10.18", 다 쓴 횟수권은 만료일도
    membershipTitle(m) {
      if (!m) return "";
      const range = [dotDate(m.start_date), dotDate(m.end_date)]
        .filter(Boolean)
        .join(" ~ ");
      return [
        membershipTypeLabel(m.type),
        membershipCountText(m),
        range,
        membershipUsedUp(m) ? `만료일 ${dotDate(m.expired_at)}` : "",
      ]
        .filter(Boolean)
        .join(" · ");
    },
    // "10.14 김코치" (결과는 따로 색을 입힌다)
    contactWho(c) {
      return [noteDate(c.at), c.author].filter(Boolean).join(" ") || "연락";
    },

    openProfile(row, event) {
      // 가운데 버튼 말고 다른 보조 버튼(오른쪽 등)은 무시한다
      const middle = event?.type === "auxclick";
      if (middle && event.button !== 1) return;
      // 글자를 끌어 고른 경우(전화번호 복사 등)는 이동하지 않는다
      if (event?.type === "click") {
        const selected = window.getSelection
          ? String(window.getSelection())
          : "";
        if (selected) return;
      }
      const path = `/profile/${encodeURIComponent(row.user_id)}`;
      // Ctrl/⌘ + 클릭과 가운데 클릭은 새 탭. 새 탭에는 방문 기록(history.state.back)이 없으므로 보던 목록 주소를 from에 실어
      // 사이드메뉴가 "회원권 추세"를 켜고 그 목록(탭·거르기 그대로)으로 돌아가게 한다(신규회원 관리와 같은 모양)
      if (middle || (event && (event.ctrlKey || event.metaKey))) {
        const href = this.$router.resolve({
          path,
          query: { from: this.$route.fullPath },
        }).href;
        window.open(href, "_blank", "noopener");
        return;
      }
      this.$router.push(path);
    },

    openContactLog(row) {
      this.logMember = {
        user_id: row.user_id,
        name: row.name,
        phone_number: row.phone_number,
      };
    },
    // 저장한 기록(POST 응답)으로 그 줄의 최근 연락만 바꾼다. 목록을 다시 읽지 않는다(설계 R20, DB 읽기 0)
    onContactSaved(note) {
      const member = this.logMember;
      this.logMember = null;
      // 줄의 키(user_id)와 같은 값을 쓴다
      const userId = (member && member.user_id) || (note && note.user_id);
      if (!userId) return;
      // 저장 전에 있던 쪽(위 / 아래 연락함 쪽)을 적어 두고 그 쪽에 그대로 둔다. 다시 저장해도 처음 쪽
      const prev = this.justSaved[userId];
      let part = prev && prev.part === "tail" ? "tail" : "head";
      if (!prev) {
        const row = this.rows.find((r) => r.user_id === userId);
        if (row && contactState(row.contact, this.today) === "done")
          part = "tail";
      }
      const contact = contactFromNote(note);
      if (contact) {
        this.savedContacts = {
          ...this.savedContacts,
          [userId]: { contact, ms: Date.now() },
        };
      }
      this.justSaved = {
        ...this.justSaved,
        [userId]: {
          part,
          at: contact ? contact.at : "",
          filter: this.contactValue,
        },
      };
      saveSaved(this.justSaved, this.savedContacts);
      this.$emit("contact-saved", { userId, note });
    },

    // TrendsView가 이탈 위험 탭을 떠나는 주소 바뀜(beforeRouteUpdate)에서 모달을 닫는다(쓰던 글은 그 전에 confirmDiscard로 물음)
    closeContactLog() {
      this.logMember = null;
    },
    // TrendsView가 다른 화면으로 나갈 때(beforeRouteLeave)·새로 고침·탭 닫기(beforeunload)에 묻는다(NewMembersView와 같은 모양)
    hasDraft() {
      const modal = this.$refs.contactLog;
      return !!(modal && modal.hasDraft());
    },
    confirmDiscard() {
      const modal = this.$refs.contactLog;
      return modal ? modal.confirmDiscard() : true;
    },
    isDialing() {
      const modal = this.$refs.contactLog;
      return !!(modal && modal.isDialing());
    },
  },
};
</script>

<style scoped>
.ar-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
  color: #fff;
}

/* ---------------- 상태 ---------------- */
.state {
  padding: 40px 16px;
  border-radius: 10px;
  border: 1px dashed #2a2a2a;
  text-align: center;
  color: #c8c8c8;
  font-size: 15px;
}

/* ---------------- 거르기 ---------------- */
.ar-filters {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.filter-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
}

.filter-k {
  flex-shrink: 0;
  width: 32px;
  font-size: 13px;
  color: #8e8e8e;
}

.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.filter-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  background: #1b1d1e;
  color: #9d9d9d;
  border: 1px solid #333;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.filter-btn:hover:not(.active) {
  background: #252728;
  color: #fff;
}

.filter-btn.active {
  background: #78ff47;
  color: #000;
  border-color: #78ff47;
  font-weight: 600;
}

.filter-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.filter-btn .count {
  margin-left: 4px;
  font-weight: 700;
}

.filter-note {
  font-size: 12px;
  color: #8e8e8e;
}

/* 짧은 정의: "만료 임박 14일 안에 … · 재등록 대기 …" (좁으면 줄을 바꾼다) */
.ar-defs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 10px 12px;
  list-style: none;
  border-radius: 8px;
  background: #0a0a0a;
  font-size: 13px;
  line-height: 1.5;
  color: #9d9d9d;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.ar-defs b {
  margin-right: 4px;
  font-weight: 600;
  color: #e5e5e5;
}

/* ---------------- 묶음 ---------------- */
.ar-tier {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.tier-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 400;
}

.tier-title {
  font-weight: 700;
}

.ar-tier--urgent .tier-title {
  color: #ef4444;
}

.tier-count {
  font-weight: 700;
}

.tier-note {
  font-size: 12px;
  color: #8e8e8e;
}

.tier-toggle {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.tier-toggle:hover:not(:disabled) {
  background: #252728;
  border-color: #4a4c4d;
}

.tier-toggle:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.tier-toggle:disabled {
  cursor: default;
  color: #8e8e8e;
}

.caret {
  width: 12px;
  color: #9d9d9d;
}

.tier-empty {
  margin: 0;
  padding: 12px;
  border-radius: 8px;
  background: #0a0a0a;
  font-size: 14px;
  color: #9d9d9d;
}

.ar-truncated {
  margin: 0;
  font-size: 13px;
  color: #9d9d9d;
  text-align: center;
}

/* ---------------- 표 ---------------- */
.ar-table {
  container: ar-list / inline-size;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

/* 칸 최소 너비: 전화 "010-1234-5678", 배지 "D-13 만료 임박", "10회권 0/10회 · 다 씀 (만료일 11.30)"(두 줄),
   "10.14 김코치 · 부재". 특이사항은 두 줄까지 보이고(말줄임) 가장 먼저 좁아진다. 1366px 노트북에서 1fr(118px)은
   "허리 디스크 — …"처럼 건강 메모가 여덟 자에서 잘려 이유 칸 다음으로 넓게 둔다 */
.ar-row {
  display: grid;
  grid-template-columns:
    minmax(108px, 1fr) /* 이름 · 전화 */
    minmax(150px, 1.6fr) /* 이유 */
    minmax(124px, 1.25fr) /* 수강권 */
    minmax(56px, 0.55fr) /* 최근 출석 */
    minmax(128px, 1.35fr) /* 최근 연락 */
    minmax(56px, 1.4fr) /* 특이사항 */
    76px; /* 연락 기록 버튼 */
  column-gap: 10px;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
}

.ar-head {
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 12px;
  color: #8e8e8e;
  border: 1px solid transparent;
  border-bottom-color: #262626;
}

.ar-item {
  background: #0a0a0a;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}

.ar-item:hover {
  background: #121314;
  border-color: #333;
}

.ar-item:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: -2px;
}

/* 최근 7일 안에 연락이 된 줄: 맨 아래, 바탕을 페이지와 같게 하고 이름만 회색으로 낮춘다. 줄 전체를 투명하게(0.55) 하면
   빨간 배지 2.3:1·전화번호 2.9:1로 글자가 4.5:1 아래였다(리뷰 4차). 배지·전화·연락 글자의 색은 그대로 둔다(빨간 배지만 아래처럼
   조금 밝게) */
.ar-item.done {
  background: transparent;
  border-color: #232323;
}

.ar-item.done .name {
  color: #a3a3a3;
  font-weight: 600;
}

/* 흐린 줄의 바탕(#151718)에서 빨간 배지 #ef4444는 4.3:1이라 조금 밝게(5.8:1, 리뷰 5차). 보통 줄(#0a0a0a)은 4.8:1 그대로 */
.ar-item.done .badge.tone-danger {
  color: #f87171;
}

.ar-item.done:hover {
  background: #121314;
}

.cell {
  min-width: 0;
}

/* 카드에서만 보이는 칸 이름 */
.cell-k {
  display: none;
}

.primary {
  font-size: 14px;
  line-height: 1.45;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary.wrap {
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.primary.ended {
  color: #c8c8c8;
}

.secondary {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.4;
  color: #9d9d9d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* "없음"·"—": 정보라 #0a0a0a 위 6:1, 흐린 줄(#151718) 위 5.5:1(리뷰 5차: #6b7280은 4.1:1·3.7:1) */
.dim {
  color: #8e8e8e;
}

/* 표에서는 이름과 "방금" 배지를 한 줄에 둔다(긴 이름은 말줄임): 배지가 다음 줄로 내려가면 줄이 24px 커져 아래 줄이 모두
   밀렸다(리뷰 4차). 카드(≤ 860px)는 칸이 넓어 접혀도 된다 */
.name-line {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px 6px;
  white-space: normal;
}

.name {
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  flex: 0 1 auto;
}

.ar-item:hover .name {
  color: #78ff47;
}

.just-saved {
  flex-shrink: 0;
}

/* 표: "방금"만 보이고 말 전체는 화면 읽기 프로그램에(title에도) */
.just-saved-full {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.badges,
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chips {
  margin-top: 4px;
}

.contact-line {
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.contact-age {
  margin-left: 6px;
  font-size: 12px;
  color: #8e8e8e;
  white-space: nowrap;
}

.contact-content {
  display: none;
}

.text-safe {
  color: #22c55e;
}

.text-warning {
  color: #fbbf24;
}

.text-danger {
  color: #ef4444;
}

/* 특이사항·연락 내용 앞 30자: 줄 높이가 이미 두 줄(이름·전화)이라 그 안에 들게 특이사항은 두 줄, 연락 내용은 연락 줄 아래
   한 줄까지 보이고 넘치면 말줄임(내용 전체는 칸 title). 리뷰 5차: 연락 내용을 두 줄까지 보여, 내용을 남겨 저장하면 그 줄이
   17~26px 커져 아래 줄이 모두 밀렸다 */
.note-text,
.snippet {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.snippet {
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.note-text {
  color: #e5e5e5;
}

.cell-action {
  display: flex;
  justify-content: flex-end;
}

.log-btn {
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.log-btn:hover {
  border-color: #78ff47;
  color: #78ff47;
  background: #151718;
}

.log-btn:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

/* 배지·칩 (tone 이름은 lib/memberNotes.js와 같다) */
.badge,
.chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 100%;
  min-height: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #c8c8c8;
  box-sizing: border-box;
}

.badge {
  font-weight: 600;
}

.chip {
  border-style: dashed;
}

.badge.tone-danger {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
}

.badge.tone-warning {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.12);
  border-color: rgba(251, 191, 36, 0.35);
}

.badge.tone-safe {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
}

.badge.tone-info,
.chip.tone-info {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.12);
  border-color: rgba(96, 165, 250, 0.35);
}

.badge.tone-muted {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
  border-color: rgba(156, 163, 175, 0.3);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------------- 반응형 ---------------- */

/* 표가 빠듯한 너비(821~940px): 칸 사이·줄 안쪽 여백을 줄인다 */
@container ar-list (max-width: 940px) {
  .ar-row {
    column-gap: 6px;
    padding-left: 8px;
    padding-right: 8px;
  }
}

/*
 * 사이드메뉴가 보이는 1101~1260px 화면처럼 표가 좁아지면(≤ 820px, 화면 > 860px) 카드 대신 두 줄 표로 줄인다:
 * 둘째 줄에 특이사항(이름·이유 아래)과 최근 출석(수강권·연락 아래)을 칸 이름과 함께 한 줄로 둔다
 * (신규회원 관리 표와 같은 규칙). 표 최소 너비는 약 600px
 */
@media (min-width: 861px) {
  @container ar-list (max-width: 820px) {
    .ar-row {
      grid-template-columns:
        minmax(104px, 1fr) /* 이름 · 전화 */
        minmax(140px, 1.5fr) /* 이유 */
        minmax(116px, 1.2fr) /* 수강권 */
        minmax(120px, 1.3fr) /* 최근 연락 */
        76px; /* 연락 기록 버튼 */
      column-gap: 6px;
      row-gap: 6px;
      padding-left: 8px;
      padding-right: 8px;
    }

    .h-attendance,
    .h-note {
      display: none;
    }

    .cell-note {
      grid-row: 2;
      grid-column: 1 / 3;
    }

    .cell-attendance {
      grid-row: 2;
      grid-column: 3 / 5;
    }

    .cell-note,
    .cell-attendance {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }

    .cell-note .cell-k,
    .cell-attendance .cell-k {
      display: block;
      flex-shrink: 0;
      font-size: 12px;
      color: #8e8e8e;
    }

    .cell-note .primary,
    .cell-attendance .primary {
      min-width: 0;
      font-size: 13px;
    }
  }
}

/* 화면 ≤ 860px(설계 4.2절): 줄을 카드로 쌓는다 */
@media (max-width: 860px) {
  .filter-line {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .filter-k {
    width: auto;
  }

  /* 글자 너비만큼 나눠 가져 "횟수 소진 임박 5"가 두 줄로 깨지지 않게 한다(모자라면 다음 줄로) */
  .filter-btn {
    flex: 1 1 auto;
    padding: 0 10px;
  }

  .filter-group {
    width: 100%;
  }

  .ar-defs {
    flex-direction: column;
  }

  .ar-head {
    display: none;
  }

  .ar-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
    row-gap: 12px;
    align-items: start;
    padding: 14px;
  }

  .ar-item {
    border-color: #1f1f1f;
  }

  .cell-k {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    color: #8e8e8e;
  }

  .cell-name,
  .cell-reasons,
  .cell-contact,
  .cell-note,
  .cell-action {
    grid-column: 1 / -1;
  }

  .cell-name {
    padding-bottom: 10px;
    border-bottom: 1px solid #1f1f1f;
  }

  .name {
    font-size: 17px;
  }

  .name-line {
    flex-wrap: wrap;
  }

  /* 카드: 이름 칸이 넓어 "방금 연락함"·"방금 기록 · 부재"를 다 보인다 */
  .just-saved-short {
    display: none;
  }

  .just-saved-full {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    clip-path: none;
  }

  .badge,
  .chip {
    font-size: 13px;
    min-height: 24px;
    white-space: normal;
  }

  /* 카드에서는 내용 앞 30자 대신 내용을 두 줄까지 */
  .snippet {
    display: none;
  }

  .contact-content {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.45;
    color: #c8c8c8;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  /* 카드에서는 마우스 올림(title)이 없으므로 특이사항을 세 줄까지 보인다 */
  .note-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  .cell-action {
    justify-content: stretch;
  }

  .log-btn {
    width: 100%;
    height: 40px;
    font-size: 14px;
  }
}
</style>
